/**
 * "Copy Short Form": the one-liner the office writes on the chart, in plain
 * English so it needs no legend.
 *
 *   Recall every 12 mo (rolling)
 *   Fee guide: 2026 (current) · Specialist fee: no · Molar composites (23322): yes · Sealants: ___
 *   Exam every 24 mo (rolling) · Pan every 36 mo (rolling) · Bitewings every 12 mo · Hygiene instruction: ___
 *   Perio & endo: 80% (in Basic) · Denture reline: ___ · Ortho: not covered
 *   Maximums per person · Combined max: yes · Dependents covered to age ___ · Students to age ___
 *   Missing tooth clause: ___ · Alternate benefit: ___ · Implants: ___
 *   Sep 15/26 — Smilepass (ProviderConnect)
 *
 * Same content and order as the paper template (fee guide, specialist fee,
 * 23322, sealant, OE, pan, BW, OHI, reline, ortho / lifetime max, age limit,
 * dependants, students, date – who) — only the labels changed — plus what a
 * full breakdown (Cleer's) also states: perio & endo, the combined maximum and
 * the clauses.
 */
import dayjs from "dayjs";

import { BLANK, NO_MAX_STATED } from "./exportModel";
import { alternateBenefitWords } from "../../../lib/planDetails";

const SEP = " · ";

// "Recall every 9 mo (rolling)", the carrier's own age split ("Recall every 6 mo
// under 19, every 9 mo 19+"), or a per-year allowance ("Recall 2 per year, 5 mo
// apart"). Never a plan-wide age cap — that belongs on the dependants line.
function recallLine(fam) {
  if (!fam || !fam.row || fam.unavailable) return `Recall every ${BLANK} mo`;
  if (fam.recallSplit && fam.recallSplit.length) {
    const rolling = /R$/.test(fam.frequency) ? " (rolling)" : "";
    return `Recall ${fam.recallSplit.map((s) => `every ${s.months} mo ${s.who}`).join(", ")}${rolling}`;
  }
  const count = Number(fam.row.max_occurrences);
  if (fam.calendar && count > 1) {
    return `Recall ${count} per year${fam.minGapMonths ? `, ${fam.minGapMonths} mo apart` : ""}`;
  }
  if (!fam.cycleMonths) return `Recall every ${BLANK} mo`;
  const rolling = /R$/.test(fam.frequency) ? " (rolling)" : "";
  return `Recall every ${fam.cycleMonths} mo${rolling}`;
}

function yesNoWithAge(fam) {
  const w = fam.yesNoWord;
  return w === "yes" && fam.ageLimit ? `yes to age ${fam.ageLimit}` : w;
}

function frequencyLabel(name, fam) {
  return `${name} ${fam.frequencyWords}`;
}

function examAndPan(exam, pan) {
  if (exam.frequencyWords !== BLANK && exam.frequencyWords === pan.frequencyWords) {
    return [`Exam & pan ${exam.frequencyWords}`];
  }
  return [frequencyLabel("Exam", exam), frequencyLabel("Pan", pan)];
}

function hygieneInstruction(fam) {
  if (fam.yesNoWord === "yes" && /^1\/LTM$/.test(fam.frequency)) return "once per lifetime";
  if (fam.yesNoWord === "yes" && fam.frequencyWords !== BLANK) return `yes, ${fam.frequencyWords}`;
  return fam.yesNoWord;
}

// "yes, paid as an alternate": the carrier allows white fillings on molars but
// pays the silver rate.
function molarComposites(fam) {
  const alt = fam.row ? alternateBenefitWords(fam.row.alternate_benefit) : "";
  return alt && fam.yesNoWord !== BLANK ? `${fam.yesNoWord}, ${alt.toLowerCase()}` : fam.yesNoWord;
}

// "80%", or "endo 80%, perio 50%" when the two differ — the rate a root canal
// or perio surgery is paid at, which the Basic card can hide — plus whether the
// plan files them under Basic.
function perioEndo(f, details) {
  const word = (pct) => (pct === "N" ? "not covered" : pct);
  const endo = f.endodontics.pct;
  const perio = f.perioSurgery.pct !== BLANK ? f.perioSurgery.pct : f.perioScaling.pct;
  let rate = BLANK;
  if (endo !== BLANK && endo === perio) rate = word(endo);
  else if (endo !== BLANK || perio !== BLANK) rate = `endo ${word(endo)}, perio ${word(perio)}`;
  const inBasic = details.perio_endo_in_basic;
  const tier = inBasic === "Yes" ? "in Basic" : inBasic === "No" ? "not in Basic" : "";
  if (rate === BLANK) return tier || BLANK;
  return tier ? `${rate} (${tier})` : rate;
}

// "yes" when basic and major draw on one maximum; "no" when the pools were
// worked out and they don't; blank until someone knows.
function combinedMax(maxima) {
  return maxima.combined === true ? "yes" : maxima.combined === false ? "no" : BLANK;
}

function reline(fam) {
  if (fam.pct !== BLANK && fam.pct !== "N") return fam.pct;
  return fam.yesNoWord;
}

function ortho(fam, maxima, details) {
  const d = details || {};
  const ageLimit = fam.ageLimit || d.ortho_age_max;
  const pay = d.ortho_payment && d.ortho_payment !== "Unknown" ? `, ${d.ortho_payment.toLowerCase()}` : "";
  const age = (ageLimit ? `, to age ${ageLimit}` : "") + pay;
  if (fam.pct === "N" || maxima.ortho.off || maxima.ortho.max === "$0.00") return "Ortho: not covered";
  // The row's own % first, else the card's (manual entries carry it on the
  // card) — or the card's status when the carrier gave no % ("Predetermination
  // required"), which follows the maximum as a clause, not a rate.
  const pctWord = fam.pct !== BLANK ? fam.pct : maxima.ortho.words;
  const fromStatus = fam.pct === BLANK && maxima.ortho.pct === BLANK && !!maxima.ortho.status;
  const atWord = pctWord === BLANK ? "" : fromStatus ? `, ${pctWord.toLowerCase()}` : ` at ${pctWord}`;
  if (maxima.ortho.noMaxStated) return `Ortho: ${pctWord}, ${NO_MAX_STATED}${age}`;
  if (maxima.ortho.max === "Unlimited") return `Ortho: unlimited${atWord}${age}`;
  if (maxima.ortho.max !== BLANK) {
    const kind = maxima.ortho.lifetime ? "lifetime max" : "per year";
    return `Ortho: ${maxima.ortho.max} ${kind}${atWord}${age}`;
  }
  if (pctWord !== BLANK) return `Ortho: ${pctWord}${age}`;
  return `Ortho: ${BLANK}`;
}

export function renderShortFormText(model) {
  const { family: f, plan, maxima, exceptions, provenance, planNote } = model;
  const lines = [];

  // The per-policy note comes first: for a phoned-in breakdown it is the
  // headline ("Combined Maximum: $1500 per calendar year", "This policy is NOT
  // ACTIVE"), and it is what the office wrote down for itself.
  if (planNote) {
    if (planNote.active === false) lines.push("PLAN NOT ACTIVE (per plan note)");
    lines.push(`Plan note: ${planNote.lines.join(" / ")}`);
  }

  lines.push(recallLine(f.recall));
  lines.push([
    `Fee guide: ${plan.feeGuideWords}`,
    `Specialist fee: ${plan.specialistFee.words}`,
    `Molar composites (${f.molarComposites.code || "23322"}): ${molarComposites(f.molarComposites)}`,
    `Sealants: ${yesNoWithAge(f.sealants)}`,
  ].join(SEP));
  lines.push([
    ...examAndPan(f.exam, f.pan),
    frequencyLabel("Bitewings", f.bitewings),
    `Hygiene instruction: ${hygieneInstruction(f.ohi)}`,
  ].join(SEP));
  const d = plan.details || {};
  lines.push([
    `Perio & endo: ${perioEndo(f, d)}`,
    `Denture reline: ${reline(f.reline)}`,
    ortho(f.ortho, maxima, plan.details),
  ].join(SEP));
  lines.push([
    ...(d.coverage_scope && d.coverage_scope !== "Unknown" ? [d.coverage_scope] : []),
    ...(d.benefit_period_type && d.benefit_period_type !== "Unknown" ? [d.benefit_period_type] : []),
    ...(d.max_scope && d.max_scope !== "Unknown" ? [`Maximums ${d.max_scope.toLowerCase()}`] : []),
    `Combined max: ${combinedMax(maxima)}`,
    `Dependents covered to age ${plan.depChildAge || BLANK}`,
    `Students to age ${plan.studentAge || BLANK}`,
  ].concat(plan.memberAge ? [`Plan age limit ${plan.memberAge}`] : []).join(SEP));
  // The clauses the office must know before quoting major work; blank until asked.
  const lower = (w) => (w ? w.toLowerCase() : BLANK);
  lines.push([
    `Missing tooth clause: ${lower(d.missing_tooth_clause)}`,
    `Alternate benefit: ${lower(d.alternate_benefit)}`,
    `Implants: ${lower(d.implants_coverage)}`,
  ].join(SEP));
  const when = provenance.dateShort
    || (dayjs(model.exportedAt || undefined).isValid() ? dayjs(model.exportedAt || undefined).format("MMM D/YY") : BLANK);
  lines.push(`${when} — Smilepass (${provenance.sourceLabel})`);
  // The chart note stays short: grouped rules, each clipped, at most a handful.
  const MAX_NOTES = 8;
  const CLIP = 160;
  exceptions.slice(0, MAX_NOTES).forEach((e) => {
    const text = e.text.length > CLIP ? `${e.text.slice(0, CLIP - 1).trim()}…` : e.text;
    const who = e.all ? "" : e.codes.length ? ` (${e.codes.join(", ")})` : "";
    lines.push(`Note: ${text}${who}`);
  });
  if (exceptions.length > MAX_NOTES) {
    lines.push(`Note: +${exceptions.length - MAX_NOTES} more — see the detailed text`);
  }

  return lines.join("\n");
}
